import { useState } from 'react';
import { useAssignClaimRequest } from './useAssignClaimRequest';
import { useCountPendingClaimRequests } from './useCountPendingClaimRequests';
import { useCreateIncident } from './useCreateIncident';
import { ClaimRequestStatus } from '../../../domain/values';
import {
    countryCodeOptionValues,
    countryCodeValueToEnum,
    defaultCountryCode,
} from '../../../domain/values/CountryCode';
import { CreateIncidentDto } from '../../../infraestructure/repositories/dtos/CreateIncidentDto';
import { useHref } from '../../hooks';
import { useCountryPreferences } from '../../hooks/useCountryPreferences';
import Inbox from '../../organisms/inbox/Inbox';
import { useModuleTranslation } from '../../utils/useModuleTranslation';
import './inbox-page.modules.scss';
import { formatToDayMonth } from '../../utils/DateUtils';

const CountryCodeOptions = countryCodeOptionValues();

const InboxPage = () => {
    const { t } = useModuleTranslation();
    const { toDataEntry, toVetView, toCoverages, toOnHold, toDocumentLibrary } = useHref();
    const { getCountryCodeLocalStorage, setCountryCodeLocalStorage } = useCountryPreferences();
    const initialiseCountryCode = (): string => {
        const initialValue = getCountryCodeLocalStorage() || defaultCountryCode;
        setCountryCodeLocalStorage(initialValue);
        return initialValue;
    };
    const [countryCode, setCountryCode] = useState<string>(initialiseCountryCode());
    const numberOfPendingClaimRequest = useCountPendingClaimRequests(countryCode);
    const {
        assignNewAssessment,
        assignNewIncident,
        assignReadyToReviewIncident,
        assignNewPreventionClaim,
        assignPreventionClaimWithNewDocumentation,
    } = useAssignClaimRequest();

    const { newIncident } = useCreateIncident();

    const handleAssignNewAssessment = async () => {
        const claimRequestKey = await assignNewAssessment();
        toVetView(claimRequestKey);
    };
    const handleAssignNewIncident = async () => {
        const claimRequestKey = await assignNewIncident(countryCode);
        toDataEntry(claimRequestKey);
    };
    const handleAssignReadyToReviewIncident = async () => {
        const claimRequestKey = await assignReadyToReviewIncident();
        toDataEntry(claimRequestKey);
    };
    const handleCountryCodeSelector = (value: string) => {
        setCountryCode(value);
        setCountryCodeLocalStorage(countryCodeValueToEnum(value)!);
    };

    const handleCreateNewIncident = async (policyNumber: string) => {
        const toCreate: CreateIncidentDto = {
            policyIdentifier: policyNumber,
            description: '...',
            reserve: true,
        };
        const claimRequestKey = await newIncident(toCreate);
        toDataEntry(claimRequestKey);
    };

    const handleSearchIncident = (policyNumber: string) => {
        toDocumentLibrary(policyNumber);
    };

    const handleAssignNewPreventionClaim = async () => {
        const claimRequest = await assignNewPreventionClaim(countryCode);
        if (claimRequest.status == ClaimRequestStatus.READY_FOR_VET_ANALYSIS) {
            history.pushState('', '', `/vetview/incidents/${claimRequest.key}`);
            toCoverages(claimRequest.key);
        } else toDataEntry(claimRequest.key);
    };

    const handleAssignPreventionClaimWithNewDocumentation = async () => {
        const claimRequestKey = await assignPreventionClaimWithNewDocumentation();
        toDataEntry(claimRequestKey);
    };

    const claimManagementItems = [
        {
            type: 'group' as const,
            title: t('inbox_page.filtered_by_country'),
        },
        {
            type: 'item' as const,
            title: t('inbox_page.new_incident'),
            count: numberOfPendingClaimRequest.numberOfNewIncidents,
            reservedCount: numberOfPendingClaimRequest.numberOfNewIncidentsReserved,
            detail: numberOfPendingClaimRequest.oldestDateOfNewIncidents
                ? formatToDayMonth(numberOfPendingClaimRequest.oldestDateOfNewIncidents)
                : undefined,
            onClick: handleAssignNewIncident,
            disabled: numberOfPendingClaimRequest.numberOfNewIncidents <= 0,
        },
        {
            type: 'item' as const,
            title: t('inbox_page.new_prevention'),
            count: numberOfPendingClaimRequest.numberOfNewPreventionClaims,
            reservedCount: numberOfPendingClaimRequest.numberOfNewPreventionClaimsReserved,
            detail: numberOfPendingClaimRequest.oldestDateOfNewPreventionClaims
                ? formatToDayMonth(numberOfPendingClaimRequest.oldestDateOfNewPreventionClaims)
                : undefined,
            onClick: handleAssignNewPreventionClaim,
            disabled: numberOfPendingClaimRequest.numberOfNewPreventionClaims <= 0,
        },
        {
            type: 'group' as const,
            title: t('inbox_page.all_countries'),
        },
        {
            type: 'item' as const,
            title: t('inbox_page.new_document'),
            count: numberOfPendingClaimRequest.numberOfReadyToReviewIncidentsGlobal,
            reservedCount: numberOfPendingClaimRequest.numberOfReadyToReviewIncidentsGlobalReserved,
            detail: numberOfPendingClaimRequest.oldestDateOfReadyToReviewIncidentsGlobal
                ? formatToDayMonth(numberOfPendingClaimRequest.oldestDateOfReadyToReviewIncidentsGlobal)
                : undefined,
            onClick: handleAssignReadyToReviewIncident,
            disabled: numberOfPendingClaimRequest.numberOfReadyToReviewIncidentsGlobal <= 0,
        },
        {
            type: 'item' as const,
            title: t('inbox_page.vet_assessment'),
            count: numberOfPendingClaimRequest.numberOfNewAssessmentsGlobal,
            reservedCount: numberOfPendingClaimRequest.numberOfNewAssessmentsGlobalReserved,
            detail: numberOfPendingClaimRequest.oldestDateOfNewAssessmentsGlobal
                ? formatToDayMonth(numberOfPendingClaimRequest.oldestDateOfNewAssessmentsGlobal)
                : undefined,
            onClick: handleAssignNewAssessment,
            disabled: numberOfPendingClaimRequest.numberOfNewAssessmentsGlobal <= 0,
        },
        {
            type: 'item' as const,
            title: t('inbox_page.prevention_with_new_documentation'),
            count: numberOfPendingClaimRequest.numberOfPreventionClaimsWithNewDocumentationGlobal,
            reservedCount: numberOfPendingClaimRequest.numberOfPreventionClaimsWithNewDocumentationGlobalReserved,
            detail: numberOfPendingClaimRequest.oldestDateOfPreventionClaimsWithNewDocumentationGlobal
                ? formatToDayMonth(numberOfPendingClaimRequest.oldestDateOfPreventionClaimsWithNewDocumentationGlobal)
                : undefined,
            onClick: handleAssignPreventionClaimWithNewDocumentation,
            disabled: numberOfPendingClaimRequest.numberOfPreventionClaimsWithNewDocumentationGlobal <= 0,
        },
        {
            type: 'item' as const,
            title: t('inbox_page.on_hold'),
            count: numberOfPendingClaimRequest.numberOfOnHoldClaimRequestGlobal,
            detail: numberOfPendingClaimRequest.oldestDateOfOnHoldClaimRequestGlobal
                ? formatToDayMonth(numberOfPendingClaimRequest.oldestDateOfOnHoldClaimRequestGlobal)
                : undefined,
            actionName: t('inbox_page.see_list'),
            onClick: () => toOnHold(),
            disabled: numberOfPendingClaimRequest.numberOfOnHoldClaimRequestGlobal <= 0,
        },
    ];

    return (
        <section className={'inbox-page-container'}>
            <Inbox
                title={t('inbox_page.page_title')}
                menuItems={claimManagementItems}
                onAdd={handleCreateNewIncident}
                onSearch={handleSearchIncident}
                countryCode={countryCode}
                onUpdateCountryCode={handleCountryCodeSelector}
                countryCodeOptions={CountryCodeOptions}
            />
        </section>
    );
};

export { InboxPage };
